import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Typography,
  Paper,
  Stack,
  Menu,
  MenuItem,
  Chip,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';

import DashboardIcon from '@mui/icons-material/Dashboard';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PaymentsIcon from '@mui/icons-material/Payments';
import InventoryIcon from '@mui/icons-material/Inventory2';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import BrandLogo from '../components/BrandLogo';
import PrivacyPolicyDialog from '../components/PrivacyPolicyDialog';
import api from '../services/api';

const statusConfig = {
  pending: {
    label: 'Pending',
    bg: '#FFF7ED',
    color: '#EA580C',
    icon: <AccessTimeOutlinedIcon />,
  },
  confirmed: {
    label: 'Confirmed',
    bg: '#DBEAFE',
    color: '#1D4ED8',
    icon: <CheckCircleOutlineOutlinedIcon />,
  },
  packed: {
    label: 'Packed',
    bg: '#F3E8FF',
    color: '#7E22CE',
    icon: <Inventory2OutlinedIcon />,
  },
  assigned_to_dlp: {
    label: 'Assigned To DLP',
    bg: '#E0E7FF',
    color: '#4338CA',
    icon: <LocalShippingOutlinedIcon />,
  },
  pickup_scheduled: {
    label: 'Pickup Scheduled',
    bg: '#ECFEFF',
    color: '#0891B2',
    icon: <AccessTimeOutlinedIcon />,
  },
  picked_up: {
    label: 'Picked Up',
    bg: '#DBEAFE',
    color: '#2563EB',
    icon: <AssignmentTurnedInOutlinedIcon />,
  },
  at_warehouse: {
    label: 'At Warehouse',
    bg: '#F1F5F9',
    color: '#475569',
    icon: <Inventory2OutlinedIcon />,
  },
  out_for_delivery: {
    label: 'Out For Delivery',
    bg: '#FEF3C7',
    color: '#B45309',
    icon: <LocalShippingOutlinedIcon />,
  },
  delivered: {
    label: 'Delivered',
    bg: '#DCFCE7',
    color: '#16A34A',
    icon: <CheckCircleOutlineOutlinedIcon />,
  },
  failed_delivery: {
    label: 'Failed Delivery',
    bg: '#FEE2E2',
    color: '#DC2626',
    icon: <ErrorOutlineOutlinedIcon />,
  },
  returned: {
    label: 'Returned',
    bg: '#F1F5F9',
    color: '#334155',
    icon: <ReplayOutlinedIcon />,
  },
  cancelled: {
    label: 'Cancelled',
    bg: '#FEE2E2',
    color: '#B91C1C',
    icon: <CancelOutlinedIcon />,
  },
};

function getStatusConfig(status) {
  return (
    statusConfig[status] || {
      label: formatStatus(status),
      bg: '#F1F5F9',
      color: '#475569',
      icon: <AccessTimeOutlinedIcon />,
    }
  );
}

function formatStatus(status = '') {
  return String(status || '')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatDateTime(value) {
  if (!value) return '-';

  return new Intl.DateTimeFormat('en-LK', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export default function LandingPage() {
  const navigate = useNavigate();

  const [loginAnchor, setLoginAnchor] = useState(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const [trackingInput, setTrackingInput] = useState('');
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState('');

  const [trackingResult, setTrackingResult] = useState(null);
  const [phoneTrackingData, setPhoneTrackingData] = useState(null);
  const [selectedPhoneOrderLoading, setSelectedPhoneOrderLoading] =
    useState(false);

  const openLoginMenu = Boolean(loginAnchor);

  const handleOpenLoginMenu = (event) => {
    setLoginAnchor(event.currentTarget);
  };

  const handleCloseLoginMenu = () => {
    setLoginAnchor(null);
  };

  const goTo = (path) => {
    handleCloseLoginMenu();
    navigate(path);
  };

  const handleTrackOrder = async (page = 1) => {
    try {
      setTrackingError('');
      setTrackingResult(null);
      setPhoneTrackingData(null);

      const cleanValue = trackingInput.trim();

      if (!cleanValue) {
        setTrackingError(
          'Please enter your Tracking ID, Waybill ID, Order ID, or Customer Phone Number.'
        );
        return;
      }

      setTrackingLoading(true);

      const res = await api.get(`/tracking/${encodeURIComponent(cleanValue)}`, {
        params: {
          page,
          pageSize: 5,
        },
      });

      if (res.data.type === 'phone_list') {
        setPhoneTrackingData(res.data.data);
        return;
      }

      setTrackingResult(res.data.data);
    } catch (error) {
      setTrackingError(
        error.response?.data?.message ||
          'Unable to find tracking details. Please check your ID, phone number, and try again.'
      );
    } finally {
      setTrackingLoading(false);
    }
  };

  const handlePhonePageChange = (nextPage) => {
    if (!phoneTrackingData?.pagination) return;

    const totalPages = phoneTrackingData.pagination.totalPages || 1;

    if (nextPage < 1 || nextPage > totalPages) {
      return;
    }

    handleTrackOrder(nextPage);
  };

  const handleViewPhoneOrder = async (orderId) => {
    try {
      setTrackingError('');
      setTrackingResult(null);
      setSelectedPhoneOrderLoading(true);

      const res = await api.get(`/tracking/${orderId}`);

      setTrackingResult(res.data.data);
    } catch (error) {
      setTrackingError(
        error.response?.data?.message ||
          'Unable to load this order tracking history.'
      );
    } finally {
      setSelectedPhoneOrderLoading(false);
    }
  };

  const features = [
    {
      icon: <DashboardIcon />,
      title: 'Admin Control Panel',
      text: 'Manage sellers, DLPs, orders, approvals, reports, and complete delivery operations.',
    },
    {
      icon: <InventoryIcon />,
      title: 'Seller Order Management',
      text: 'Sellers can receive orders, track deliveries, print waybills, and monitor payments.',
    },
    {
      icon: <QrCodeScannerIcon />,
      title: 'Barcode Dispatch',
      text: 'Scan waybill barcodes and automatically update order status to dispatched.',
    },
    {
      icon: <PaymentsIcon />,
      title: 'COD & Finance Flow',
      text: 'Track COD collection, bank deposits, finance verification, and merchant payments.',
    },
  ];

  const workflow = [
    'Order Created',
    'Pickup',
    'Hub / Sorting',
    'Dispatch Scan',
    'Delivery',
    'COD / Return',
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f6f7fb',
        color: '#111827',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 8px 25px rgba(15,23,42,0.04)',
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              height: { xs: 76, md: 86 },
            }}
          >
            <Box
              onClick={() => navigate('/')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <BrandLogo width={210} height={88} />
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="text"
                onClick={() => setPrivacyOpen(true)}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 800,
                  color: '#1d4ed8',
                  px: 2,
                  display: { xs: 'none', md: 'inline-flex' },
                  '&:hover': {
                    bgcolor: '#eff6ff',
                  },
                }}
              >
                Privacy Policy
              </Button>

              <Button
                variant="outlined"
                onClick={() => navigate('/seller/register')}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 800,
                  px: 2.4,
                  display: { xs: 'none', sm: 'inline-flex' },
                  borderColor: '#1d4ed8',
                  color: '#1d4ed8',
                  '&:hover': {
                    borderColor: '#1e40af',
                    bgcolor: '#eff6ff',
                  },
                }}
              >
                Become a Seller
              </Button>

              <Button
                variant="contained"
                endIcon={<KeyboardArrowDownIcon />}
                onClick={handleOpenLoginMenu}
                sx={{
                  borderRadius: 999,
                  px: 2.8,
                  textTransform: 'none',
                  fontWeight: 900,
                  bgcolor: '#1d4ed8',
                  boxShadow: '0 8px 20px rgba(29,78,216,0.28)',
                  '&:hover': {
                    bgcolor: '#1e40af',
                    boxShadow: '0 10px 24px rgba(29,78,216,0.34)',
                  },
                }}
              >
                Login
              </Button>

              <Menu
                anchorEl={loginAnchor}
                open={openLoginMenu}
                onClose={handleCloseLoginMenu}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: 3,
                    minWidth: 220,
                    boxShadow: '0 20px 50px rgba(15,23,42,0.18)',
                  },
                }}
              >
                <MenuItem onClick={() => goTo('/admin/login')}>
                  Admin Login
                </MenuItem>

                <MenuItem onClick={() => goTo('/seller/login')}>
                  Seller Login
                </MenuItem>

                <MenuItem onClick={() => goTo('/dlp/login')}>
                  DLP Login
                </MenuItem>
              </Menu>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box
        sx={{
          background:
            'linear-gradient(135deg, #0f172a 0%, #1e3a8a 45%, #2563eb 100%)',
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 380,
            height: 380,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.08)',
            right: -120,
            top: -120,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            width: 260,
            height: 260,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.08)',
            left: -90,
            bottom: -90,
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            py: { xs: 7, md: 10 },
          }}
        >
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mb: 2,
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Chip
                  label="Pickup"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    fontWeight: 800,
                  }}
                />
                <Chip
                  label="Sorting Center"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    fontWeight: 800,
                  }}
                />
                <Chip
                  label="COD Settlement"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    fontWeight: 800,
                  }}
                />
              </Stack>

              <Typography
                variant="h2"
                fontWeight={900}
                sx={{
                  fontSize: { xs: 38, md: 60 },
                  lineHeight: 1.08,
                  letterSpacing: '-0.04em',
                  mb: 2,
                  maxWidth: 720,
                }}
              >
                Professional delivery operations in one platform.
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255,255,255,0.86)',
                  maxWidth: 680,
                  lineHeight: 1.7,
                  mb: 4,
                  fontSize: { xs: 16, md: 20 },
                }}
              >
                Zypio helps manage seller orders, pickup, hub sorting, waybill
                barcode scanning, delivery partner dispatch, COD collection,
                returns, and finance settlement.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  size="large"
                  variant="contained"
                  onClick={handleOpenLoginMenu}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    bgcolor: '#fff',
                    color: '#1d4ed8',
                    borderRadius: 999,
                    px: 4,
                    py: 1.4,
                    fontWeight: 900,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: '#eff6ff',
                    },
                  }}
                >
                  Login to Portal
                </Button>

                <Button
                  size="large"
                  variant="outlined"
                  onClick={() => navigate('/seller/register')}
                  sx={{
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.48)',
                    borderRadius: 999,
                    px: 4,
                    py: 1.4,
                    fontWeight: 900,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#fff',
                      bgcolor: 'rgba(255,255,255,0.08)',
                    },
                  }}
                >
                  Register as Seller
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: 5,
                  bgcolor: 'rgba(255,255,255,0.96)',
                  boxShadow: '0 30px 80px rgba(0,0,0,0.25)',
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={900}
                  color="#111827"
                  gutterBottom
                >
                  Live Workflow
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  From merchant order to delivery, return, and finance settlement.
                </Typography>

                <Stack spacing={1.5}>
                  {workflow.map((item, index) => (
                    <Stack
                      key={item}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        bgcolor: '#f8fafc',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          bgcolor: index === 3 ? '#16a34a' : '#1d4ed8',
                          color: '#fff',
                          display: 'grid',
                          placeItems: 'center',
                          fontWeight: 900,
                          fontSize: 14,
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Typography fontWeight={900} color="#111827">
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <TrackingSearchSection
            trackingInput={trackingInput}
            setTrackingInput={setTrackingInput}
            trackingLoading={trackingLoading}
            trackingError={trackingError}
            trackingResult={trackingResult}
            phoneTrackingData={phoneTrackingData}
            selectedPhoneOrderLoading={selectedPhoneOrderLoading}
            handleTrackOrder={handleTrackOrder}
            handlePhonePageChange={handlePhonePageChange}
            handleViewPhoneOrder={handleViewPhoneOrder}
          />
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography
            variant="h3"
            fontWeight={900}
            sx={{
              mb: 1,
              fontSize: { xs: 34, md: 48 },
              letterSpacing: '-0.03em',
            }}
          >
            Built for real delivery teams
          </Typography>

          <Typography color="text.secondary" sx={{ maxWidth: 720, mx: 'auto' }}>
            Manage every stage of the delivery workflow from one professional
            dashboard.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature) => (
            <Grid item xs={12} sm={6} md={3} key={feature.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  height: '100%',
                  border: '1px solid #e5e7eb',
                  transition: '0.25s',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 18px 45px rgba(15,23,42,0.10)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    bgcolor: '#dbeafe',
                    color: '#1d4ed8',
                    display: 'grid',
                    placeItems: 'center',
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>

                <Typography variant="h6" fontWeight={900} gutterBottom>
                  {feature.title}
                </Typography>

                <Typography
                  color="text.secondary"
                  variant="body2"
                  lineHeight={1.7}
                >
                  {feature.text}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ bgcolor: '#fff', borderTop: '1px solid #e5e7eb' }}>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="h4" fontWeight={900} gutterBottom>
                Access your portal
              </Typography>

              <Typography color="text.secondary">
                Choose the correct login based on your role.
              </Typography>
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/admin/login')}
                    sx={{
                      py: 1.5,
                      borderRadius: 3,
                      fontWeight: 900,
                      textTransform: 'none',
                    }}
                  >
                    Admin Login
                  </Button>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/seller/login')}
                    sx={{
                      py: 1.5,
                      borderRadius: 3,
                      fontWeight: 900,
                      textTransform: 'none',
                    }}
                  >
                    Seller Login
                  </Button>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/dlp/login')}
                    sx={{
                      py: 1.5,
                      borderRadius: 3,
                      fontWeight: 900,
                      textTransform: 'none',
                    }}
                  >
                    DLP Login
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary" align="center">
              © {new Date().getFullYear()} Zypio. Smart Delivery Management
              System.
            </Typography>

            <Typography
              variant="body2"
              onClick={() => setPrivacyOpen(true)}
              sx={{
                color: '#1d4ed8',
                fontWeight: 800,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Privacy Policy
            </Typography>
          </Stack>
        </Container>
      </Box>

      <PrivacyPolicyDialog
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
      />
    </Box>
  );
}

function TrackingSearchSection({
  trackingInput,
  setTrackingInput,
  trackingLoading,
  trackingError,
  trackingResult,
  phoneTrackingData,
  selectedPhoneOrderLoading,
  handleTrackOrder,
  handlePhonePageChange,
  handleViewPhoneOrder,
}) {
  return (
    <Box sx={{ mt: { xs: 5, md: 7 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.2, md: 3 },
          borderRadius: 5,
          bgcolor: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.24)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Stack spacing={2.5}>
          <Box textAlign="center">
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                color: '#ffffff',
              }}
            >
              Track Your Delivery
            </Typography>

            <Typography
              sx={{
                color: 'rgba(255,255,255,0.86)',
                mt: 0.6,
                fontWeight: 600,
              }}
            >
              Enter your Tracking ID, Waybill ID, Order ID, or Customer Phone
              Number.
            </Typography>
          </Box>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            sx={{
              maxWidth: 820,
              mx: 'auto',
              width: '100%',
            }}
          >
            <TextField
              fullWidth
              value={trackingInput}
              onChange={(e) => setTrackingInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleTrackOrder(1);
                }
              }}
              placeholder="Tracking ID / Waybill ID / Order ID / Customer Phone Number"
              disabled={trackingLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ color: '#f97316' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: '#ffffff',
                  minHeight: 64,
                  boxShadow: '0 10px 24px rgba(15,23,42,0.12)',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#fb923c',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#f97316',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputBase-input': {
                  fontWeight: 700,
                  color: '#111827',
                },
              }}
            />

            <Button
              variant="contained"
              size="large"
              onClick={() => handleTrackOrder(1)}
              disabled={trackingLoading}
              startIcon={
                trackingLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SearchOutlinedIcon />
                )
              }
              sx={{
                minWidth: { xs: '100%', md: 150 },
                minHeight: 64,
                borderRadius: 3,
                fontWeight: 900,
                textTransform: 'none',
                fontSize: 16,
                bgcolor: '#f97316',
                color: '#ffffff',
                boxShadow: '0 12px 26px rgba(249,115,22,0.35)',
                '&:hover': {
                  bgcolor: '#ea580c',
                  boxShadow: '0 14px 30px rgba(234,88,12,0.42)',
                },
                '&.Mui-disabled': {
                  bgcolor: '#fed7aa',
                  color: '#ffffff',
                },
              }}
            >
              {trackingLoading ? 'Tracking...' : 'Track'}
            </Button>
          </Stack>


          {trackingError ? (
            <Alert
              severity="error"
              sx={{
                maxWidth: 820,
                mx: 'auto',
                width: '100%',
                borderRadius: 3,
                fontWeight: 700,
              }}
            >
              {trackingError}
            </Alert>
          ) : null}

          {phoneTrackingData ? (
            <PhoneOrderListCard
              data={phoneTrackingData}
              selectedPhoneOrderLoading={selectedPhoneOrderLoading}
              onPageChange={handlePhonePageChange}
              onViewOrder={handleViewPhoneOrder}
            />
          ) : null}

          {trackingResult ? <TrackingResultCard result={trackingResult} /> : null}
        </Stack>
      </Paper>
    </Box>
  );
}

function PhoneOrderListCard({
  data,
  selectedPhoneOrderLoading,
  onPageChange,
  onViewOrder,
}) {
  const orders = data.orders || [];
  const pagination = data.pagination || {};
  const page = pagination.page || 1;
  const totalPages = pagination.totalPages || 1;
  const total = pagination.total || 0;

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 920,
        mx: 'auto',
        width: '100%',
        mt: 1,
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        bgcolor: '#ffffff',
        color: '#111827',
      }}
    >
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1}
        >
          <Box>
            <Typography variant="h5" fontWeight={900}>
              Orders Found
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              We found {total} order{total === 1 ? '' : 's'} for this customer
              phone number. Latest orders are shown first.
            </Typography>
          </Box>

          <Chip
            label={`Page ${page} of ${totalPages}`}
            sx={{
              bgcolor: '#FFF7ED',
              color: '#EA580C',
              fontWeight: 900,
            }}
          />
        </Stack>

        <Stack spacing={1.5}>
          {orders.map((order) => {
            const config = getStatusConfig(order.status);

            return (
              <Paper
                key={order.id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid #e5e7eb',
                  bgcolor: '#f8fafc',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  spacing={2}
                >
                  <Box sx={{ flex: 1 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      sx={{ mb: 1 }}
                    >
                      <Typography fontWeight={900}>Order #{order.id}</Typography>

                      <Typography variant="body2" color="text.secondary">
                        {order.tracking_number || 'No tracking number'}
                      </Typography>
                    </Stack>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={800}
                        >
                          Customer
                        </Typography>
                        <Typography fontWeight={900}>
                          {order.customer_name || '-'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={800}
                        >
                          Phone
                        </Typography>
                        <Typography fontWeight={900}>
                          {order.customer_phone || '-'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={800}
                        >
                          Item
                        </Typography>
                        <Typography fontWeight={900}>
                          {order.item_name || '-'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={800}
                        >
                          Created
                        </Typography>
                        <Typography fontWeight={900}>
                          {formatDateTime(order.created_at)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                  >
                    <Chip
                      icon={config.icon}
                      label={config.label}
                      sx={{
                        bgcolor: config.bg,
                        color: config.color,
                        fontWeight: 900,
                        '& .MuiChip-icon': {
                          color: config.color,
                        },
                      }}
                    />

                    <Button
                      variant="contained"
                      disabled={selectedPhoneOrderLoading}
                      onClick={() => onViewOrder(order.id)}
                      sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 900,
                        bgcolor: '#f97316',
                        '&:hover': {
                          bgcolor: '#ea580c',
                        },
                      }}
                    >
                      {selectedPhoneOrderLoading
                        ? 'Loading...'
                        : 'View Tracking'}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>

        <Divider />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1.5}
        >
          <Typography variant="body2" color="text.secondary" fontWeight={700}>
            Showing latest orders first
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 900,
              }}
            >
              Previous
            </Button>

            <Button
              variant="outlined"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 900,
              }}
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}

function TrackingResultCard({ result }) {
  const order = result.order || {};
  const history = result.history || [];
  const currentConfig = getStatusConfig(order.status);

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 920,
        mx: 'auto',
        width: '100%',
        mt: 1,
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        bgcolor: '#fff',
        color: '#111827',
      }}
    >
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h5" fontWeight={900}>
              Tracking History
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Order #{order.id} • {order.tracking_number || 'No tracking number'}
            </Typography>
          </Box>

          <Chip
            icon={currentConfig.icon}
            label={currentConfig.label}
            sx={{
              bgcolor: currentConfig.bg,
              color: currentConfig.color,
              fontWeight: 900,
              '& .MuiChip-icon': {
                color: currentConfig.color,
              },
            }}
          />
        </Stack>

        <Grid container spacing={2}>
          <InfoBox label="Customer" value={order.customer_name || '-'} />
          <InfoBox label="Phone" value={order.customer_phone || '-'} />
          <InfoBox label="Item" value={order.item_name || '-'} />
          <InfoBox
            label="Delivery Service"
            value={order.delivery_service_label || 'Normal Delivery'}
          />
        </Grid>

        <Divider />

        <Box>
          <Typography variant="h6" fontWeight={900} sx={{ mb: 2 }}>
            Tracking Timeline
          </Typography>

          <Stack spacing={1.6}>
            {history.map((item, index) => {
              const config = getStatusConfig(item.status || item.new_status);

              return (
                <Stack
                  key={`${item.status}-${item.changed_at}-${index}`}
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '50%',
                      bgcolor: config.bg,
                      color: config.color,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {config.icon}
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      pb: 1.6,
                      borderBottom:
                        index === history.length - 1
                          ? 'none'
                          : '1px dashed #e5e7eb',
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      spacing={0.5}
                    >
                      <Typography fontWeight={900} color={config.color}>
                        {config.label}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        {formatDateTime(item.changed_at)}
                      </Typography>
                    </Stack>

                    {item.note ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.4 }}
                      >
                        {item.note}
                      </Typography>
                    ) : null}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function InfoBox({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Box
        sx={{
          p: 1.6,
          borderRadius: 3,
          bgcolor: '#F8FAFC',
          border: '1px solid #E5E7EB',
          height: '100%',
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={800}>
          {label}
        </Typography>

        <Typography fontWeight={900} sx={{ mt: 0.5 }}>
          {value}
        </Typography>
      </Box>
    </Grid>
  );
}